import { call, put, takeLatest } from "redux-saga/effects";
import * as types from "./DemandTableActionType";
import * as actions from "./DemandTableActions";
import * as services from "./DemandTableServices";
import Swal from "sweetalert2";
import { SweetAlert } from "../../../components/sweetalert/SweetAlert.js";
import { downloadBase64File } from "../../../utils/fileDownloads";

// Fetch Volume Trend Reports Dropdown List
function* fetchVolumeTrendReportsDDSaga() {
  try {
    const response = yield call(services.getVolumeTrendReportsDDList);
    yield put(actions.fetchVolumeTrendReportsDDSuccess(response));
  } catch (error) {
    yield put(actions.fetchVolumeTrendReportsDDFailure(error.message));
    SweetAlert.error({
      title: "Error!",
      text: "Failed to fetch Volume Trend Reports dropdown list",
      showConfirmButton: true,
    });
  }
}

// Fetch Reports Tab Data with Pagination
function* fetchReportsTabSaga(action) {
  try {
    const response = yield call(
      services.getDemandTableReportsWithPagination,
      action.payload,
    );
    yield put(actions.fetchReportsTabSuccess(response));
  } catch (error) {
    yield put(actions.fetchReportsTabFailure(error.message));
    SweetAlert.error({
      title: "Error!",
      text: "Failed to fetch Reports data",
      showConfirmButton: true,
    });
  }
}

// Fetch Onboarding Tab Data
function* fetchOnboardingTabSaga(action) {
  try {
    const response = yield call(
      services.getDemandInputOnboardingData,
      action.payload,
    );
    yield put(actions.fetchOnboardingTabSuccess(response));
  } catch (error) {
    yield put(actions.fetchOnboardingTabFailure(error.message));
    SweetAlert.error({
      title: "Error!",
      text: "Failed to fetch Onboarding data",
      showConfirmButton: true,
    });
  }
}

// Notes Edit Saga
function* fetchNotesEditSaga(action) {
  try {
    const response = yield call(services.getNotesEditData, action.payload);
    yield put(actions.fetchNotesEditSuccess(response));
  } catch (error) {
    yield put(actions.fetchNotesEditFailure(error.message));
    SweetAlert.error({
      title: "Error!",
      text: "Failed to fetch Notes data",
      showConfirmButton: true,
    });
  }
}

// Notes Save Saga
function* fetchNotesSaveSaga(action) {
  try {
    const response = yield call(services.saveNotesData, action.payload);
    if (response?.success === false) {
      const errorMessage = response?.errorMessage || "Failed to save Notes data";
      yield put(actions.fetchNotesSaveFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
        showConfirmButton: true,
      });
      return;
    }
    yield put(actions.fetchNotesSaveSuccess(response));
    SweetAlert.success({
      icon: "success",
      title: "Success",
      text: response?.resultMessage || "Notes saved successfully",
      showConfirmButton: true,
    });
  } catch (error) {
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.message ||
      "Failed to save Notes data";
    yield put(actions.fetchNotesSaveFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
      showConfirmButton: true,
    });
  }
}

// Export Reports Saga
function* exportReportsSaga(action) {
  try {
    let exportAPIURL, payload, moduleName, fileName;
    if (action.payload && action.payload.exportAPIURL) {
      exportAPIURL = action.payload.exportAPIURL;
      payload = action.payload.payload;
      moduleName = action.payload.moduleName || "DemandTable";
      fileName = action.payload.fileName;
    } else {
      // fallback for old usage
      exportAPIURL = undefined;
      payload = action.payload;
      moduleName = "DemandTable";
      fileName = action.payload.fileName || undefined;
    }

    // If exportAPIURL is provided, use it, else use default service
    let response;
    if (exportAPIURL) {
      response = yield call(services.exportReportsData, exportAPIURL, payload);
    } else {
      response = yield call(services.exportReportsData, payload);
    }

    // Try to get base64 data
    const base64Data = response?.values || response;
    yield call(downloadBase64File, base64Data, moduleName, fileName);
    yield put(actions.exportReportsSuccess(response));
  } catch (error) {
    console.error("Export error:", error);
    const errorMessage =
      error?.response?.data?.errorMessage || error?.message || "Export failed";
    yield put(actions.exportReportsFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
      showConfirmButton: true,
    });
  }
}

// Export Onboarding Saga
function* exportOnboardingSaga(action) {
  try {
    const { DemandTableId, fileName } = action.payload;
    const response = yield call(services.exportOnboardingData, {
      DemandTableId,
    });
    const base64Data = response?.values || response;
    yield call(downloadBase64File, base64Data, "OnboardingReport", fileName);
    yield put({ type: types.EXPORT_ONBOARDING_SUCCESS });
    yield call([SweetAlert, SweetAlert.success], {
      title: "Success",
      text:
        response?.resultMessage ||
        "Successfully Exported Demand Input Onboarding Data.",
    });
  } catch (error) {
    console.error("Onboarding export error:", error);
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.message ||
      "Demand Input Onboarding export failed. Please try again.";
    yield put({ type: types.EXPORT_ONBOARDING_FAILURE });
    yield call([SweetAlert, SweetAlert.error], {
      title: "Failed",
      text: errorMessage,
    });
  }
}

// Fetch DemandTableID
function* fetchDemandTableByReportSaga(action) {
  try {
    const { reportId, createdBy } = action.payload;

    // SAFETY CHECK
    if (!reportId || !createdBy) {
      throw new Error("Invalid ReportId or CreatedBy");
    }

    const response = yield call(
      services.getDemandTableByReport,
      reportId,
      createdBy,
    );

    const demandTableId = response?.values?.DemandTableID;
    if (!demandTableId) throw new Error("DemandTableID missing");

    yield put(actions.fetchDemandTableByReportSuccess(demandTableId));

    yield put(
      actions.updateDemandTableReducer({
        isVolumeTrendLocked: true,
        activeTab: "demandInput",
      }),
    );
  } catch (error) {
    console.error(error);
    SweetAlert.error({
      title: "Error!",
      text: "Failed to fetch Demand Table ID",
      showConfirmButton: true,
    });
  }
}

// Fetch Demand Input Filter Dropdown List
function* fetchGetDemandInputFilterDDListSaga(action) {
  try {
    const response = yield call(
      services.getDemandInputFilterDDList,
      action.payload.ReportID,
      action.payload.DemandTableID,
    );
    if (response?.success) {
      yield put(
        actions.fetchGetDemandInputFilterDDListSuccess(response?.values),
      );
    } else {
      yield put(
        actions.fetchGetDemandInputFilterDDListFailure(
          "No dropdown data found",
        ),
      );
    }
  } catch (error) {
    console.error("Demand Input Filter Dropdown Fetch Error:", error);
    yield put(
      actions.fetchGetDemandInputFilterDDListFailure(
        "Failed to fetch dropdown data",
      ),
    );
    SweetAlert.error({
      title: "Error!",
      text: "Failed to fetch dropdown data",
      showConfirmButton: true,
    });
  }
}

// Fetch Worklist By Site Dropdown List
function* fetchWorklistBySiteDDListSaga(action) {
  try {
    const response = yield call(
      services.getWorklistBySiteDropDown,
      action.payload.SiteIDs,
    );
    if (response?.success) {
      yield put(actions.fetchWorklistBySiteDDListSuccess(response?.values));
    } else {
      yield put(
        actions.fetchWorklistBySiteDDListFailure("No dropdown data found"),
      );
    }
  } catch (error) {
    console.error("Worklist By Site Dropdown Fetch Error:", error);
    yield put(
      actions.fetchWorklistBySiteDDListFailure(
        "Failed to fetch dropdown data",
      ),
    );
  }
}

function* fetchWorklistWithSiteMappingDDListSaga() {
  try {
    const response = yield call(services.getWorklistWithSiteMappingDropDown);
    if (response?.success) {
      yield put(actions.fetchWorklistWithSiteMappingDDListSuccess(response?.values));
    } else {
      yield put(
        actions.fetchWorklistWithSiteMappingDDListFailure("No dropdown data found"),
      );
    }
  } catch (error) {
    console.error("Worklist With Site Mapping Dropdown Fetch Error:", error);
    yield put(
      actions.fetchWorklistWithSiteMappingDDListFailure(
        "Failed to fetch dropdown data",
      ),
    );
  }
}

function* fetchWorklistByWorklistGroupDDListSaga(action) {
  try {
    const response = yield call(
      services.getWorklistByWorklistGroupDropDown,
      action.payload.WorklistGroupIDs,
    );
    if (response?.success) {
      yield put(
        actions.fetchWorklistByWorklistGroupDDListSuccess(response?.values),
      );
    } else {
      yield put(
        actions.fetchWorklistByWorklistGroupDDListFailure(
          "No dropdown data found",
        ),
      );
    }
  } catch (error) {
    console.error("Worklist By Worklist Group Dropdown Fetch Error:", error);
    yield put(
      actions.fetchWorklistByWorklistGroupDDListFailure(
        "Failed to fetch dropdown data",
      ),
    );
  }
}

// Delete Demand Report Saga
function* deleteDemandReportSaga(action) {
  try {
    // Accept both DemandTableId and DemandTableID for compatibility
    const demandTableId =
      action.payload.demandTableId || action.payload.DemandTableID;
    const response = yield call(services.deleteDemandReport, demandTableId);
    if (response?.success) {
      yield put(actions.deleteDemandReportSuccess(response));
      SweetAlert.success({
        title: "Deleted!",
        text: response?.resultMessage || "Demand report has been deleted.",
        showConfirmButton: true,
      });

      // Refresh the reports table after delete with pagination params
      yield put(
        actions.fetchReportsTabRequest({
          SkipNoOfRecords: 0,
          PageSize: 100,
          sSearch: "",
          sortColumn: "DemandTableID",
          sortColumnDir: "desc",
        }),
      );

      // Call meta.onSuccess if provided (for UI to handle dropdown and tab reset)
      if (action.meta && typeof action.meta.onSuccess === "function") {
        yield action.meta.onSuccess();
      }
    } else {
      yield put(actions.deleteDemandReportFailure("Delete failed"));
      SweetAlert.error({
        title: "Error!",
        text: response?.message || "Failed to delete demand report",
        showConfirmButton: true,
      });

      if (action.meta && typeof action.meta.onError === "function") {
        yield action.meta.onError(
          response?.message || "Failed to delete demand report",
        );
      }
    }
  } catch (error) {
    yield put(actions.deleteDemandReportFailure(error.message));
    SweetAlert.error({
      title: "Error!",
      text: error.message || "Failed to delete demand report",
      showConfirmButton: true,
    });

    if (action.meta && typeof action.meta.onError === "function") {
      yield action.meta.onError(
        error.message || "Failed to delete demand report",
      );
    }
  }
}

// Demand Table (6th Tab) Saga - paginated data
function* fetchDemandTableTabSaga(action) {
  try {
    const response = yield call(
      services.getDemandTableWithPagination,
      action.payload,
    );

    yield put({
      type: "FETCH_DEMAND_TABLE_TAB_SUCCESS",
      payload: response,
    });
  } catch (error) {
    yield put({
      type: "FETCH_DEMAND_TABLE_TAB_FAILURE",
      error,
    });
    SweetAlert.error({
      title: "Error!",
      text: "Failed to fetch Demand Table data",
      showConfirmButton: true,
    });
  }
}

// All Tab Binding - Get by Id Saga
function* fetchAllTabsDemandTableDataByIdSaga(action) {
  try {
    const response = yield call(services.getAllTabsDataById, action.payload);

    yield put(
      actions.fetchDemandTableByIdSuccess({
        ...response,
        TabNumber: action.payload.TabNumber, // attach TabNumber
      }),
    );
  } catch (error) {
    yield put(actions.fetchDemandTableByIdFailure(error.message));
    SweetAlert.error({
      title: "Error!",
      text: "Failed to fetch data",
      showConfirmButton: true,
    });
  }
}

// Export Termed CSV Saga
function* exportTermedSaga(action) {
  try {
    const { fileName, payload } = action.payload;

    const response = yield call(services.exportTermedCSV, payload);

    const base64Data = response?.values || response;
    yield call(downloadBase64File, base64Data, "TermedReport", fileName);

    yield put({ type: types.EXPORT_TERMED_SUCCESS });
    yield call([SweetAlert, SweetAlert.success], {
      title: "Success",
      text: response?.resultMessage || "Successfully Exported Termed Data.",
    });
  } catch (error) {
    console.error("Termed export error:", error);
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.message ||
      "Termed export failed. Please try again.";
    yield put({ type: types.EXPORT_TERMED_FAILURE });
    yield call([SweetAlert, SweetAlert.error], {
      title: "Failed",
      text: errorMessage,
    });
  }
}

// Export Offboarding CSV Saga
function* exportOffboardingSaga(action) {
  try {
    const { fileName, payload } = action.payload;

    const response = yield call(services.exportOffboardingCSV, payload);

    const base64Data = response?.values || response;
    yield call(downloadBase64File, base64Data, "OffboardingReport", fileName);

    yield put({ type: types.EXPORT_OFFBOARDING_SUCCESS });
    yield call([SweetAlert, SweetAlert.success], {
      title: "Success",
      text: response?.resultMessage || "Successfully Exported Offboarding Data.",
    });
  } catch (error) {
    console.error("Offboarding export error:", error);
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.message ||
      "Offboarding export failed. Please try again.";
    yield put({ type: types.EXPORT_OFFBOARDING_FAILURE });
    yield call([SweetAlert, SweetAlert.error], {
      title: "Failed",
      text: errorMessage,
    });
  }
}

// Export daylight saving Ended CSV Saga
function* exportDaylightSavingEndedSaga(action) {
  try {
    const { fileName, payload } = action.payload;

    const response = yield call(services.exportDaylightSavingEndedCSV, payload);

    const base64Data = response?.values || response;
    yield call(
      downloadBase64File,
      base64Data,
      "DaylightSavingEndedReport",
      fileName,
    );

    yield put({ type: types.EXPORT_ENDED_SUCCESS });
    yield call([SweetAlert, SweetAlert.success], {
      title: "Success",
      text:
        response?.resultMessage ||
        "Successfully Exported Daylight Saving Ended Data.",
    });
  } catch (error) {
    console.error("Daylight Saving Ended export error:", error);
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.message ||
      "Daylight Saving Ended export failed. Please try again.";
    yield put({ type: types.EXPORT_ENDED_FAILURE });
    yield call([SweetAlert, SweetAlert.error], {
      title: "Failed",
      text: errorMessage,
    });
  }
}

// Export daylight saving Upcoming CSV Saga
function* exportDaylightSavingUpcomingSaga(action) {
  try {
    const { fileName, payload } = action.payload;

    const response = yield call(
      services.exportDaylightSavingUpcomingCSV,
      payload,
    );

    const base64Data = response?.values || response;
    yield call(
      downloadBase64File,
      base64Data,
      "DaylightSavingUpcomingReport",
      fileName,
    );

    yield put({ type: types.EXPORT_UPCOMING_SUCCESS });
    yield call([SweetAlert, SweetAlert.success], {
      title: "Success",
      text:
        response?.resultMessage ||
        "Successfully Exported Daylight Saving Upcoming Data.",
    });
  } catch (error) {
    console.error("Daylight Saving Upcoming export error:", error);
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.message ||
      "Daylight Saving Upcoming export failed. Please try again.";
    yield put({ type: types.EXPORT_UPCOMING_FAILURE });
    yield call([SweetAlert, SweetAlert.error], {
      title: "Failed",
      text: errorMessage,
    });
  }
}

// Practice bulk update
function* bulkUpdatePracticeSaga(action) {
  try {
    const response = yield call(
      services.bulkUpdateDemandChangesPracticeData,
      action.payload,
    );

    if (response?.success === false) {
      const errorMessage = response?.errorMessage || "Failed to update Practice tab.";
      yield put(actions.bulkUpdatePracticeFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
        showConfirmButton: true,
      });
      return;
    }

    yield put(actions.bulkUpdatePracticeSuccess(response));

    SweetAlert.success({
      title: "Saved!",
      text: response?.resultMessage || "Your updated data has been saved.",
    });

    // Callback to reset state in component
    if (action.meta?.onSuccess) {
      action.meta.onSuccess();
    }
  } catch (error) {
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.message ||
      "Failed to update Practice tab.";
    yield put(actions.bulkUpdatePracticeFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
      showConfirmButton: true,
    });
  }
}

// Site bulk update
function* bulkUpdateSiteSaga(action) {
  try {
    const response = yield call(
      services.bulkUpdateDemandChangesSiteData,
      action.payload,
    );

    if (response?.success === false) {
      const errorMessage = response?.errorMessage || "Failed to update Site tab.";
      yield put(actions.bulkUpdateSiteFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
        showConfirmButton: true,
      });
      return;
    }

    yield put(actions.bulkUpdateSiteSuccess(response));

    SweetAlert.success({
      title: "Saved!",
      text: response?.resultMessage || "Your updated data has been saved.",
      showConfirmButton: true,
    });

    if (action.meta?.onSuccess) {
      action.meta.onSuccess();
    }
  } catch (error) {
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.message ||
      "Failed to update Site tab.";
    yield put(actions.bulkUpdateSiteFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
      showConfirmButton: true,
    });
  }
}

// Bulk Delete Practice Saga
function* bulkDeletePracticeSaga(action) {
  try {
    const response = yield call(
      services.bulkDeleteDemandChangesPracticeData,
      action.payload,
    );
    if (response?.success === false) {
      const errorMessage = response?.errorMessage || "Failed to delete Practice data.";
      yield put(actions.bulkDeletePracticeFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
        showConfirmButton: true,
      });
      return;
    }

    yield put(actions.bulkDeletePracticeSuccess(response));

    SweetAlert.success({
      title: "Deleted!",
      text: response?.resultMessage || "Selected Practices have been deleted.",
      showConfirmButton: true,
    });
    if (action.meta?.onSuccess) {
      action.meta.onSuccess();
    }
  } catch (error) {
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.message ||
      "Failed to delete Practice data.";
    yield put(actions.bulkDeletePracticeFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
      showConfirmButton: true,
    });
  }
}

// Bulk Delete Site Saga
function* bulkDeleteSiteSaga(action) {
  try {
    const response = yield call(
      services.bulkDeleteDemandChangesSiteData,
      action.payload,
    );
    if (response?.success === false) {
      const errorMessage = response?.errorMessage || "Failed to delete Site data.";
      yield put(actions.bulkDeleteSiteFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
        showConfirmButton: true,
      });
      return;
    }

    yield put(actions.bulkDeleteSiteSuccess(response));
    SweetAlert.success({
      title: "Deleted!",
      text: response?.resultMessage || "Selected Sites have been deleted.",
      showConfirmButton: true,
    });
    if (action.meta?.onSuccess) {
      action.meta.onSuccess();
    }
  } catch (error) {
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.message ||
      "Failed to delete Site data.";
    yield put(actions.bulkDeleteSiteFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
      showConfirmButton: true,
    });
  }
}

// Export Practice Saga
function* exportPracticeSaga(action) {
  try {
    const { DemandTableId, fileName } = action.payload;
    const response = yield call(services.exportPracticeData, { DemandTableId });
    const base64Data = response?.values || response;
    yield call(downloadBase64File, base64Data, "PracticeReport", fileName);
    yield put({ type: types.EXPORT_PRACTICE_SUCCESS });
    yield call([SweetAlert, SweetAlert.success], {
      title: "Success",
      text:
        response?.resultMessage ||
        "Successfully Exported Demand Changes Practice Data.",
    });
  } catch (error) {
    console.error("Practice export error:", error);
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.message ||
      "Demand Changes Practice export failed. Please try again.";
    yield put({ type: types.EXPORT_PRACTICE_FAILURE });
    yield call([SweetAlert, SweetAlert.error], {
      title: "Failed",
      text: errorMessage,
    });
  }
}

// Export Site Saga
function* exportSiteSaga(action) {
  try {
    const { DemandTableId, fileName } = action.payload;
    const response = yield call(services.exportSiteData, { DemandTableId });
    const base64Data = response?.values || response;
    yield call(downloadBase64File, base64Data, "SiteReport", fileName);
    yield put({ type: types.EXPORT_SITE_SUCCESS });
    yield call([SweetAlert, SweetAlert.success], {
      title: "Success",
      text:
        response?.resultMessage ||
        "Successfully Exported Demand Changes Facility Data.",
    });
  } catch (error) {
    console.error("Site export error:", error);
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.message ||
      "Demand Changes Facility export failed. Please try again.";
    yield put({ type: types.EXPORT_SITE_FAILURE });
    yield call([SweetAlert, SweetAlert.error], {
      title: "Failed",
      text: errorMessage,
    });
  }
}

// Export Worklist Saga
function* exportWorklistSaga(action) {
  try {
    const { DemandTableId, fileName } = action.payload;
    const response = yield call(services.exportWorklistData, { DemandTableId });
    const base64Data = response?.values || response;
    yield call(downloadBase64File, base64Data, "WorklistReport", fileName);
    yield put({ type: types.EXPORT_WORKLIST_SUCCESS });
    yield call([SweetAlert, SweetAlert.success], {
      title: "Success",
      text:
        response?.resultMessage ||
        "Successfully Exported Demand Changes Worklist Data.",
    });
  } catch (error) {
    console.error("Worklist export error:", error);
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.message ||
      "Demand Changes Worklist export failed. Please try again.";
    yield put({ type: types.EXPORT_WORKLIST_FAILURE });
    yield call([SweetAlert, SweetAlert.error], {
      title: "Failed",
      text: errorMessage,
    });
  }
}

// Refresh Demand Table Saga
function* refreshDemandTableSaga(action) {
  try {
    const response = yield call(
      services.refreshDemandTableData,
      action.payload,
    );
    yield put(actions.refreshDemandTableSuccess(response));

    // Call dependent APIs ONLY after refresh succeeds
    yield put(
      actions.fetchDemandTableByIdRequest({
        DemandTableReportID: action.payload.DemandTableId,
        ReportId: 0,
        TabNumber: 6,
      }),
    );
    yield put(
      actions.fetchDemandTableTabRequest({
        DemandTableReportID: action.payload.DemandTableId,
        FromDate: null,
        ToDate: null,
        SkipNoOfRecords: 0,
        PageSize: 100,
        sSearch: "",
        sortColumn: "DemandTableDate",
        sortColumnDir: "asc",
        WorklistID: "",
        DayID: "",
        // Always pass the real on-screen checkbox state explicitly — previously
        // omitted entirely, letting the SP's own parameter default silently win.
        WorklistView: !!action.payload.WorklistView,
      }),
    );

    SweetAlert.success({
      title: "Refreshed!",
      text: response?.resultMessage || "Your report has been refreshed.",
      confirmButtonText: "OK",
      showConfirmButton: true,
    });
  } catch (error) {
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.message ||
      "Failed to refresh Demand Table data";
    yield put(actions.refreshDemandTableFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
      showConfirmButton: true,
    });
  }
}

// Save as Draft Saga
function* handleSaveAsDraft(action) {
  try {
    const payload = action.payload;
    const result = yield call(services.saveDraftDemandTable, payload);

    // NORMALIZE RESPONSE
    const data = result?.values ?? result;

    // ERROR CASE
    if (data?.success === false) {
      SweetAlert.error({
        title: "Oops!",
        text: data?.errorMessage,
        showConfirmButton: true,
      });

      yield put(actions.saveAsDraftFailure(data?.errorMessage));
      return; // STOP here
    }

    // SUCCESS CASE
    yield put(actions.saveAsDraftSuccess(data));

    SweetAlert.success({
      title: "Saved!",
      text: data?.resultMessage || "Successfully Drafted Demand Table Reports",
      showConfirmButton: true,
    });
    if (action.callbacks?.onSuccess) {
      action.callbacks.onSuccess();
    }
  } catch (error) {
    const apiErrorMessage =
      error?.response?.data?.errorMessage ||
      error?.message ||
      "Failed to save draft report. Please try again.";

    SweetAlert.error({
      title: "Cannot Save Draft",
      text: apiErrorMessage,
      showConfirmButton: true,
    });

    yield put(actions.saveAsDraftFailure(apiErrorMessage));
  }
}

// Publish Demand Table Saga
function* handlePublishDemandTable(action) {
  try {
    const payload = action.payload;
    const result = yield call(services.publishDemandTableReport, payload);

    // Normalize response
    const data = result?.values ?? result;

    // API-level error
    if (data?.success === false) {
      SweetAlert.error({
        title: "Publish Failed",
        text: data?.errorMessage || "Unable to publish Demand Table",
      });

      yield put(actions.publishDemandTableFailure(data?.errorMessage));
      return;
    }

    // API-level success
    yield put(actions.publishDemandTableSuccess(data));

    SweetAlert.success({
      title: "Published!",
      text:
        data?.resultMessage || "Demand Table Report published successfully.",
      showConfirmButton: true,
    });
    if (action.callbacks?.onSuccess) {
      action.callbacks.onSuccess();
    }
  } catch (error) {
    const apiErrorMessage =
      error?.response?.data?.errorMessage ||
      error?.message ||
      "Failed to publish Demand Table Report. Please try again.";

    SweetAlert.error({
      title: "Publish Error",
      text: apiErrorMessage,
      showConfirmButton: true,
    });

    yield put(actions.publishDemandTableFailure(apiErrorMessage));
  }
}

// View Demand Table Saga - combines multiple API calls
function* handleViewDemandTable(action) {
  try {
    const { DemandTableReportID, ReportId } = action.payload;

    // ---------------- API 1 ----------------
    const allTabsResponse = yield call(services.getAllTabsDataById, {
      DemandTableReportID,
      ReportId,
      TabNumber: 0,
    });

    // ---------------- API 2 ----------------
    const demandInputAccumulatedResponse = yield call(
      services.getAllTabsDataById,
      {
        DemandTableReportID,
        ReportId: 0,
        TabNumber: 1,
      },
    );

    // ---------------- API 3 ----------------
    const filterResponse = yield call(
      services.getDemandInputFilterDDList,
      ReportId,
      DemandTableReportID,
    );

    // ---------------- Update Redux ----------------
    console.log("SAGA VIEW_DEMAND_TABLE_SUCCESS payload:", {
      demandInputFilters: filterResponse,
    });

    yield put({
      type: types.VIEW_DEMAND_TABLE_SUCCESS,
      payload: {
        // API 1: All Tabs
        onboardingTabData:
          allTabsResponse?.values?.listDemandInputOnboardingData ?? [],
        demandChangesWorklistData:
          allTabsResponse?.values?.listDemandChangesWorklistData ?? [],
        demandChangesSiteData:
          allTabsResponse?.values?.listDemandChangesSiteData ?? [],
        dayLightSavingEndedData:
          allTabsResponse?.values?.listDemandInputDaylightSavingEndedData ?? [],
        dayLightSavingUpcomingData:
          allTabsResponse?.values?.listDemandInputDaylightSavingUpcomingData ??
          [],
        offboardingTableData:
          allTabsResponse?.values?.listDemandInputOffboardData ?? [],
        termedTableData:
          allTabsResponse?.values?.listDemandInputTermedData ?? [],

        // API 2: Demand Input (server-filtered/grouped rows)
        demandInputAccumulated: demandInputAccumulatedResponse?.values
          ?.DemandInputAccumulated ?? {
          Rows: [],
        },

        // API 3: Dropdowns inside Demand Input
        demandInputFilters: filterResponse,

        // UI state
        activeTab: "demandInput",
        isVolumeTrendLocked: true,
        enabledTabs: [
          "demandInput",
          "onboarding",
          "practice",
          "site",
          "offboarding",
          "termed",
          "daylightEnded",
          "daylightUpcoming",
        ],
        disabledTabs: ["reports"],
      },
    });
  } catch (error) {
    console.error("View Demand Table Error:", error);
    yield put({
      type: types.VIEW_DEMAND_TABLE_FAILURE,
      payload: error?.message || "Failed to load Demand Table",
    });
    SweetAlert.error({
      title: "Error!",
      text: "Failed to load Demand Table. Please try again.",
      showConfirmButton: true,
    });
  }
}

// Fetch Demand Changes Practice Data Saga
function* fetchDemandChangesPracticeSaga(action) {
  try {
    const response = yield call(
      services.getDemandChangesPracticeData,
      action.payload,
    );

    if (response && response.success) {
      yield put(actions.fetchDemandChangesPracticeSuccess(response));
    } else {
      const errorMessage =
        response?.errorMessage ||
        "Failed to fetch Demand Changes Practice data";
      yield put(actions.fetchDemandChangesPracticeFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
        showConfirmButton: true,
      });
    }
  } catch (error) {
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.message ||
      "Failed to fetch Demand Changes Practice data";
    yield put(actions.fetchDemandChangesPracticeFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
      showConfirmButton: true,
    });
  }
}

// Fetch Demand Changes Site Data Saga
function* fetchDemandChangesSiteSaga(action) {
  try {
    const response = yield call(
      services.getDemandChangesSiteData,
      action.payload,
    );

    if (response && response.success) {
      yield put(actions.fetchDemandChangesSiteSuccess(response));
    } else {
      const errorMessage =
        response?.errorMessage || "Failed to fetch Demand Changes Site data";
      yield put(actions.fetchDemandChangesSiteFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
        showConfirmButton: true,
      });
    }
  } catch (error) {
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.message ||
      "Failed to fetch Demand Changes Site data";
    yield put(actions.fetchDemandChangesSiteFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
      showConfirmButton: true,
    });
  }
}

// Fetch/Apply Demand Changes Worklist Data Saga
function* fetchDemandChangesWorklistSaga(action) {
  try {
    const response = yield call(
      services.getDemandChangesWorklistData,
      action.payload,
    );

    if (response && response.success) {
      yield put(actions.fetchDemandChangesWorklistSuccess(response));
    } else {
      const errorMessage =
        response?.errorMessage ||
        "Failed to fetch Demand Changes Worklist data";
      yield put(actions.fetchDemandChangesWorklistFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
        showConfirmButton: true,
      });
    }
  } catch (error) {
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.message ||
      "Failed to fetch Demand Changes Worklist data";
    yield put(actions.fetchDemandChangesWorklistFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
      showConfirmButton: true,
    });
  }
}

// Worklist bulk update
function* bulkUpdateWorklistSaga(action) {
  try {
    const response = yield call(
      services.bulkUpdateDemandChangesWorklistData,
      action.payload,
    );

    if (response?.success === false) {
      const errorMessage = response?.errorMessage || "Failed to update Worklist tab.";
      yield put(actions.bulkUpdateWorklistFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
        showConfirmButton: true,
      });
      return;
    }

    yield put(actions.bulkUpdateWorklistSuccess(response));

    SweetAlert.success({
      title: "Saved!",
      text: response?.resultMessage || "Your updated data has been saved.",
      showConfirmButton: true,
    });

    if (action.meta?.onSuccess) {
      action.meta.onSuccess();
    }
  } catch (error) {
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.message ||
      "Failed to update Worklist tab.";
    yield put(actions.bulkUpdateWorklistFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
      showConfirmButton: true,
    });
  }
}

// Bulk Delete Worklist Saga
function* bulkDeleteWorklistSaga(action) {
  try {
    const response = yield call(
      services.bulkDeleteDemandChangesWorklistData,
      action.payload,
    );
    if (response?.success === false) {
      const errorMessage = response?.errorMessage || "Failed to delete Worklist data.";
      yield put(actions.bulkDeleteWorklistFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
        showConfirmButton: true,
      });
      return;
    }

    yield put(actions.bulkDeleteWorklistSuccess(response));

    SweetAlert.success({
      title: "Deleted!",
      text: response?.resultMessage || "Selected Worklists have been deleted.",
      showConfirmButton: true,
    });
    if (action.meta?.onSuccess) {
      action.meta.onSuccess();
    }
  } catch (error) {
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.message ||
      "Failed to delete Worklist data.";
    yield put(actions.bulkDeleteWorklistFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
      showConfirmButton: true,
    });
  }
}

// Fetch Holiday Coverage Worklist Data Saga
function* fetchHolidayCoverageWorklistSaga(action) {
  try {
    const response = yield call(
      services.getHolidayCoverageWorklistData,
      action.payload,
    );

    if (response && response.success) {
      yield put(actions.fetchHolidayCoverageWorklistSuccess(response));
    } else {
      const errorMessage =
        response?.errorMessage ||
        "Failed to fetch Holiday Coverage Worklist data";
      yield put(actions.fetchHolidayCoverageWorklistFailure(errorMessage));
      SweetAlert.error({
        title: "Error!",
        text: errorMessage,
        showConfirmButton: true,
      });
    }
  } catch (error) {
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.message ||
      "Failed to fetch Holiday Coverage Worklist data";
    yield put(actions.fetchHolidayCoverageWorklistFailure(errorMessage));
    SweetAlert.error({
      title: "Error!",
      text: errorMessage,
      showConfirmButton: true,
    });
  }
}

// Watcher Saga
function* watchDemandTableSaga() {
  yield takeLatest(
    types.FETCH_VOLUME_TREND_REPORTS_DD_REQUEST,
    fetchVolumeTrendReportsDDSaga,
  );
  yield takeLatest(types.FETCH_REPORTS_TAB_REQUEST, fetchReportsTabSaga);
  yield takeLatest(types.FETCH_ONBOARDING_TAB_REQUEST, fetchOnboardingTabSaga);
  yield takeLatest(types.EXPORT_ONBOARDING_REQUEST, exportOnboardingSaga);
  yield takeLatest(types.FETCH_NOTES_EDIT_REQUEST, fetchNotesEditSaga);
  yield takeLatest(types.FETCH_NOTES_SAVE_REQUEST, fetchNotesSaveSaga);
  yield takeLatest(types.EXPORT_REPORTS_REQUEST, exportReportsSaga);
  yield takeLatest(
    types.FETCH_DEMAND_TABLE_BY_REPORT_REQUEST,
    fetchDemandTableByReportSaga,
  );
  yield takeLatest(
    types.FETCH_GET_DEMAND_INPUT_FILTER_DD_LIST_REQUEST,
    fetchGetDemandInputFilterDDListSaga,
  );
  yield takeLatest(
    types.FETCH_WORKLIST_BY_SITE_DD_LIST_REQUEST,
    fetchWorklistBySiteDDListSaga,
  );
  yield takeLatest(
    types.FETCH_WORKLIST_WITH_SITE_MAPPING_DD_LIST_REQUEST,
    fetchWorklistWithSiteMappingDDListSaga,
  );
  yield takeLatest(
    types.FETCH_WORKLIST_BY_WORKLISTGROUP_DD_LIST_REQUEST,
    fetchWorklistByWorklistGroupDDListSaga,
  );
  yield takeLatest(types.DELETE_DEMAND_REPORT_REQUEST, deleteDemandReportSaga);
  yield takeLatest(
    types.FETCH_DEMAND_TABLE_TAB_REQUEST,
    fetchDemandTableTabSaga,
  );
  yield takeLatest(
    types.FETCH_DEMAND_TABLE_BY_ID_REQUEST,
    fetchAllTabsDemandTableDataByIdSaga,
  );
  yield takeLatest(types.EXPORT_TERMED_REQUEST, exportTermedSaga);
  yield takeLatest(types.EXPORT_OFFBOARDING_REQUEST, exportOffboardingSaga);
  yield takeLatest(types.EXPORT_ENDED_REQUEST, exportDaylightSavingEndedSaga);
  yield takeLatest(
    types.EXPORT_UPCOMING_REQUEST,
    exportDaylightSavingUpcomingSaga,
  );
  yield takeLatest(types.BULK_UPDATE_PRACTICE_REQUEST, bulkUpdatePracticeSaga);
  yield takeLatest(types.BULK_UPDATE_SITE_REQUEST, bulkUpdateSiteSaga);
  yield takeLatest(types.SAVE_AS_DRAFT_REQUEST, handleSaveAsDraft);
  yield takeLatest(
    types.PUBLISH_DEMAND_TABLE_REQUEST,
    handlePublishDemandTable,
  );
  yield takeLatest(types.VIEW_DEMAND_TABLE_REQUEST, handleViewDemandTable);
  yield takeLatest(types.BULK_DELETE_PRACTICE_REQUEST, bulkDeletePracticeSaga);
  yield takeLatest(types.BULK_DELETE_SITE_REQUEST, bulkDeleteSiteSaga);
  yield takeLatest(types.EXPORT_PRACTICE_REQUEST, exportPracticeSaga);
  yield takeLatest(types.EXPORT_SITE_REQUEST, exportSiteSaga);
  yield takeLatest(types.EXPORT_WORKLIST_REQUEST, exportWorklistSaga);
  yield takeLatest(types.REFRESH_DEMAND_TABLE_REQUEST, refreshDemandTableSaga);
  yield takeLatest(
    types.FETCH_DEMAND_CHANGES_PRACTICE_REQUEST,
    fetchDemandChangesPracticeSaga,
  );
  yield takeLatest(
    types.FETCH_DEMAND_CHANGES_SITE_REQUEST,
    fetchDemandChangesSiteSaga,
  );
  yield takeLatest(
    types.FETCH_DEMAND_CHANGES_WORKLIST_REQUEST,
    fetchDemandChangesWorklistSaga,
  );
  yield takeLatest(types.BULK_UPDATE_WORKLIST_REQUEST, bulkUpdateWorklistSaga);
  yield takeLatest(types.BULK_DELETE_WORKLIST_REQUEST, bulkDeleteWorklistSaga);
  yield takeLatest(
    types.FETCH_HOLIDAY_COVERAGE_WORKLIST_REQUEST,
    fetchHolidayCoverageWorklistSaga,
  );
}

export default watchDemandTableSaga;
